"use client"

import { useCallback, useRef, useEffect, useState } from "react"

type Axis = "horizontal" | "vertical"

interface UseResizeOptions {
  /** The axis the handle drags along */
  axis: Axis
  /** Initial value in pixels */
  initial: number
  /** Hard minimum */
  min: number
  /** Hard maximum */
  max: number
  /** Snap points — if the value is within ±snapThreshold of a point it snaps */
  snapPoints?: number[]
  /** Snap threshold in px (default 5) */
  snapThreshold?: number
  /** Invert direction (drag up = bigger, drag left = bigger) */
  invert?: boolean
  /** localStorage key to persist layout */
  storageKey?: string
  /** Collapsed size — double-click toggles to/from this value */
  collapseSize?: number
}

export function useResize(options: UseResizeOptions) {
  const {
    axis,
    initial,
    min,
    max,
    snapPoints = [],
    snapThreshold = 5,
    invert = false,
    storageKey,
    collapseSize,
  } = options

  /* ── persisted / initial value (React state — only updated on mouseUp) ── */
  const [value, setValue] = useState(() => {
    if (storageKey && typeof window !== "undefined") {
      const saved = localStorage.getItem(storageKey)
      if (saved) {
        const parsed = Number(saved)
        if (!isNaN(parsed) && parsed >= min && parsed <= max) return parsed
      }
    }
    return initial
  })

  /* ── refs that live outside the React render cycle ── */
  const isDragging = useRef(false)
  const startPos = useRef(0)
  const startValue = useRef(0)
  const liveValue = useRef(value) // always holds the "truth" during drag
  const rafId = useRef(0)
  const preCollapseValue = useRef(initial)
  const targetRef = useRef<HTMLDivElement>(null) // attach to the resizable panel
  const dynamicMax = useRef(max)

  // Keep liveValue in sync when React state changes outside of a drag
  useEffect(() => {
    if (!isDragging.current) liveValue.current = value
  }, [value])

  // Persist to localStorage
  useEffect(() => {
    if (storageKey && typeof window !== "undefined") {
      localStorage.setItem(storageKey, String(value))
    }
  }, [value, storageKey])

  /* ── snap helper ── */
  const snap = useCallback(
    (v: number) => {
      for (const s of snapPoints) {
        if (Math.abs(v - s) < snapThreshold) return s
      }
      return v
    },
    [snapPoints, snapThreshold],
  )

  /* ── mousemove: direct DOM write → zero React re-renders ── */
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging.current) return
      const pos = axis === "horizontal" ? e.clientX : e.clientY
      const delta = pos - startPos.current
      const raw = invert ? startValue.current - delta : startValue.current + delta
      const clamped = Math.max(min, Math.min(dynamicMax.current, snap(raw)))
      liveValue.current = clamped

      cancelAnimationFrame(rafId.current)
      rafId.current = requestAnimationFrame(() => {
        if (targetRef.current) {
          // Bypass React — write straight to the DOM element
          const prop = axis === "horizontal" ? "width" : "height"
          targetRef.current!.style[prop] = `${clamped}px`
        } else {
          // Fallback for panels that don't attach targetRef (RAF-throttled setState)
          setValue(clamped)
        }
      })
    },
    [axis, invert, min, snap],
  )

  /* ── mouseup: commit final value to React state (single render) ── */
  const handleMouseUp = useCallback(() => {
    if (!isDragging.current) return
    isDragging.current = false
    cancelAnimationFrame(rafId.current)
    // Single state update → one React re-render
    setValue(liveValue.current)

    document.body.style.cursor = ""
    document.body.style.userSelect = ""
    const overlay = document.getElementById("resize-overlay")
    if (overlay) overlay.remove()
  }, [])

  /* ── attach / detach global listeners ── */
  useEffect(() => {
    document.addEventListener("mousemove", handleMouseMove)
    document.addEventListener("mouseup", handleMouseUp)
    return () => {
      document.removeEventListener("mousemove", handleMouseMove)
      document.removeEventListener("mouseup", handleMouseUp)
      cancelAnimationFrame(rafId.current)
    }
  }, [handleMouseMove, handleMouseUp])

  /* ── mousedown: snapshot start state + compute dynamic max ── */
  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      isDragging.current = true
      startPos.current = axis === "horizontal" ? e.clientX : e.clientY
      startValue.current = liveValue.current // read from ref, never stale

      // Dynamic max: prevent panel from exceeding container − min − divider
      if (targetRef.current?.parentElement) {
        const rect = targetRef.current.parentElement.getBoundingClientRect()
        const containerSize = axis === "horizontal" ? rect.width : rect.height
        dynamicMax.current = Math.min(max, containerSize - min - 20)
      } else {
        dynamicMax.current = max
      }

      // Lock cursor globally
      document.body.style.cursor =
        axis === "horizontal" ? "col-resize" : "row-resize"
      document.body.style.userSelect = "none"

      // Remove any stale overlay from a previous drag
      const stale = document.getElementById("resize-overlay")
      if (stale) stale.remove()

      // Full-screen overlay so iframes / code editors can't steal pointer events
      const overlay = document.createElement("div")
      overlay.id = "resize-overlay"
      overlay.style.cssText =
        "position:fixed;inset:0;z-index:9999;cursor:" +
        (axis === "horizontal" ? "col-resize" : "row-resize")
      document.body.appendChild(overlay)
    },
    [axis, max, min],
  )

  /* ── double-click: collapse / restore ── */
  const onDoubleClick = useCallback(() => {
    if (collapseSize === undefined) return
    const next =
      liveValue.current <= collapseSize
        ? preCollapseValue.current
        : ((preCollapseValue.current = liveValue.current), collapseSize)
    liveValue.current = next
    if (targetRef.current) {
      const prop = axis === "horizontal" ? "width" : "height"
      targetRef.current.style[prop] = `${next}px`
    }
    setValue(next)
  }, [collapseSize, axis])

  return { value, targetRef, onMouseDown, onDoubleClick }
}
